import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { requestLoginGreeting, clearLoginGreeting } from '../utils/festivals';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [permissions, setPermissions] = useState(() => {
    const saved = localStorage.getItem('permissions');
    return saved ? JSON.parse(saved) : {};
  });
  const [loading, setLoading] = useState(false);
  const [permsLoading, setPermsLoading] = useState(false);

  // Fetch fresh permissions from API — called on mount, route change, and after admin saves
  const loadPermissions = useCallback(async (currentUser) => {
    const u = currentUser || user;
    if (!u || u.role !== 'worker') return;
    setPermsLoading(true);
    try {
      const res = await api.get('/user-rights');
      const perms = res.data || {};
      setPermissions(perms);
      localStorage.setItem('permissions', JSON.stringify(perms));
    } catch {
      // On error keep existing permissions (don't wipe them)
    } finally {
      setPermsLoading(false);
    }
  }, [user]);

  // ── On app mount / page refresh: immediately fetch fresh permissions ─────────
  useEffect(() => {
    if (user && user.role === 'worker') {
      loadPermissions(user);
    }
  }, []); // run once on mount — covers page refresh

  const login = async (username, password) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username, password });
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      if (user.role === 'worker') {
        try {
          const pRes = await api.get('/user-rights', { headers: { Authorization: `Bearer ${token}` } });
          const perms = pRes.data || {};
          setPermissions(perms);
          localStorage.setItem('permissions', JSON.stringify(perms));
        } catch { setPermissions({}); }
      } else {
        setPermissions({});
        localStorage.removeItem('permissions');
      }
      requestLoginGreeting(); // festival greeting shows once on the dashboard after this login
      return { success: true, role: user.role };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearLoginGreeting();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setUser(null);
    setPermissions({});
  };

  // Helper: check if worker has a specific permission on a menu
  const hasPermission = (menuKey, level = 'read') => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perm = permissions[menuKey];
    if (!perm) return false;
    const levelMap = { read: 'can_read', write: 'can_write', edit: 'can_edit', delete: 'can_delete' };
    return !!perm[levelMap[level]];
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, permsLoading, permissions, hasPermission, loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

